import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin, assertLightAdmin, assertPageAction } from "@/lib/admin-guard.server";
import { cached, invalidateCached } from "@/lib/server-cache";
import { plural } from "@/lib/plural";

export const SPANISH_STATUSES = ["new", "reviewed", "contacted", "accepted", "declined"] as const;
export type SpanishStatus = (typeof SPANISH_STATUSES)[number];

export const SPANISH_STATUS_LABEL: Record<SpanishStatus, string> = {
  new: "Ny",
  reviewed: "Granskad",
  contacted: "Kontaktad",
  accepted: "Antagen",
  declined: "Avböjd",
};

export const spanishParticipantSchema = z.object({
  courseId: z.string().uuid("Välj en kurs"),
  participantName: z.string().trim().min(2, "Fyll i namn").max(120),
});


export const spanishCourseSchema = z.object({
  participants: z.array(spanishParticipantSchema).min(1).max(10),
  contactName: z.string().trim().min(2, "Fyll i namn").max(120),
  contactEmail: z.string().trim().email("Fyll i en giltig e-postadress").max(255),
  contactPhone: z.string().trim().max(60).default(""),
  startTerm: z.string().trim().max(60).default(""),
  message: z.string().trim().max(2000).default(""),
  locale: z.enum(["sv", "en", "es"]).default("sv"),
  sourcePath: z.string().trim().max(300).default(""),
  consent: z.boolean(),
  /** Honeypot – ska alltid vara tomt. */
  company: z.string().max(200).default(""),
  elapsedMs: z.number().int().nonnegative().default(0),
  sessionId: z.string().trim().max(64).default(""),
  visitorId: z.string().trim().max(64).default(""),
});

export type SpanishCourseInput = z.input<typeof spanishCourseSchema>;

/** Publikt: tar emot en anmälan till spanskakurserna. */
export const submitSpanishCourseApplication = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => spanishCourseSchema.parse(data))
  .handler(async ({ data }) => {
    if (data.company.trim() !== "" || data.elapsedMs < 1500) {
      // Spamskydd: honeypot ifylld eller skickat orimligt snabbt.
      return { ok: true as const };
    }
    if (!data.consent) throw new Error("Du behöver godkänna att vi hanterar uppgifterna.");

    // Kurs och pris hämtas serverside ur katalogen – aldrig från webbläsaren.
    const { performSpanishSubmit } = await import("@/lib/spanish-submit.server");
    await performSpanishSubmit(data);
    return { ok: true as const };

  });

/** Admin: alla anmälningar till spanskakurserna. */
export const listSpanishCourseApplications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertLightAdmin(context as never, "/admin/spanskakurser");
    const { shouldHideTestData } = await import("@/lib/admin-guard.server");
    let query = context.supabase
      .from("spanish_course_applications")
      .select("*")
      .is("deleted_at", null);
    // Testdata syns bara för superadmin.
    if (await shouldHideTestData(context as never)) query = query.eq("is_test", false);
    const { data, error } = await query.order("created_at", { ascending: false }).limit(500);
    if (error) throw new Error("Kunde inte hämta anmälningarna.");
    return { applications: data ?? [] };
  });

/** Admin: byt status på en anmälan. */
export const updateSpanishCourseApplication = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), status: z.enum(SPANISH_STATUSES) }).parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/spanskakurser");
    await (await import("@/lib/revisions.server")).saveRevision(
      context as never,
      "spanish_course_applications",
      data.id,
      "Före statusändring i admin",
    );
    const { error } = await context.supabase
      .from("spanish_course_applications")
      .update({ status: data.status })
      .eq("id", data.id);
    if (error) throw new Error("Kunde inte spara.");
    await (await import("@/lib/audit.server")).logAdminAction(context as never, {
      action: "spanish_course.status",
      entity: "spanish_course_application",
      entityId: data.id,
      summary: `Ändrade status på spanskakursanmälan till ${data.status}`,
    });
    // Arbetsflödet följer med när statusen ändras i statusraden.
    const { syncStageFromStatus } = await import("@/lib/workflow-sync.server");
    await syncStageFromStatus(
      context as never,
      "spanish",
      data.id,
      data.status,
      SPANISH_STATUS_LABEL[data.status] ?? data.status,
    );
    return { ok: true as const };
  });

/** Admin: ta bort en anmälan. */
export const deleteSpanishCourseApplication = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertPageAction(context as never, "/admin/spanskakurser", "delete");
    const { softDeleteRow } = await import("@/lib/trash.server");
    await softDeleteRow(context as never, "spanish_course_applications", data.id);
    await (await import("@/lib/audit.server")).logAdminAction(context as never, {
      action: "spanish_course.delete",
      entity: "spanish_course_application",
      entityId: data.id,
      severity: "warning",
      summary: "Tog bort en anmälan till spanskakurs",
    });
    return { ok: true as const };
  });

export type SpanishCourseOptions = {
  /** Om den publika spanskakurssidan visas i menyn och sitemapen. */
  publicPage: boolean;
  adults: boolean;
  childrenOnce: boolean;
  childrenTwice: boolean;
  terms: string[] | null;
  adultFee: number;
  childOnceFee: number;
  childTwiceFee: number;
};

export const DEFAULT_SPANISH_OPTIONS: SpanishCourseOptions = {
  publicPage: true,
  adults: true,
  childrenOnce: true,
  childrenTwice: true,
  terms: null,
  adultFee: 300,
  childOnceFee: 300,
  childTwiceFee: 570,
};

function fee(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n >= 0 && n <= 10000 ? Math.round(n) : fallback;
}

function parseSpanishOptions(value: unknown): SpanishCourseOptions {
  const v = (value ?? {}) as Partial<SpanishCourseOptions>;
  return {
    publicPage: v.publicPage !== false,
    adults: v.adults !== false,
    childrenOnce: v.childrenOnce !== false,
    childrenTwice: v.childrenTwice !== false,
    terms: Array.isArray(v.terms) ? v.terms.filter((t) => typeof t === "string") : null,
    adultFee: fee(v.adultFee, DEFAULT_SPANISH_OPTIONS.adultFee),
    childOnceFee: fee(v.childOnceFee, DEFAULT_SPANISH_OPTIONS.childOnceFee),
    childTwiceFee: fee(v.childTwiceFee, DEFAULT_SPANISH_OPTIONS.childTwiceFee),
  };
}


/** Läser kursinställningarna serverside – används av både publik hämtning och inskick. */
async function loadSpanishOptions(): Promise<SpanishCourseOptions> {
  return cached("spanish-course-options", 5 * 60_000, async () => {
    const { createClient } = await import("@supabase/supabase-js");
    const supabase = createClient(
      process.env["SUPABASE_URL"]!,
      process.env["SUPABASE_PUBLISHABLE_KEY"]!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data, error } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", "spanish_course_options")
      .maybeSingle();
    if (error) console.error("spanish_course_options read failed", error);
    return parseSpanishOptions(data?.value);
  });
}

/** Publikt: vilka kursval och terminer som är påslagna. */
export const getSpanishCourseOptions = createServerFn({ method: "GET" }).handler(async () =>
  loadSpanishOptions(),
);


/** Admin: spara vilka kursval och terminer som ska visas publikt. */
export const setSpanishCourseOptions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        publicPage: z.boolean().default(true),
        adults: z.boolean(),
        childrenOnce: z.boolean(),
        childrenTwice: z.boolean(),
        terms: z.array(z.string().max(60)).nullable().default(null),
        adultFee: z.number().int().min(0).max(10000).default(300),
        childOnceFee: z.number().int().min(0).max(10000).default(300),
        childTwiceFee: z.number().int().min(0).max(10000).default(570),
      })
      .parse(data),
  )

  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/spanskakurser");
    const { error } = await context.supabase
      .from("app_settings")
      .upsert({ key: "spanish_course_options", value: data }, { onConflict: "key" });
    if (error) {
      console.error("spanish_course_options save failed", error);
      throw new Error("Inställningarna kunde inte sparas.");
    }
    invalidateCached("spanish-course-options");
    await (await import("@/lib/audit.server")).logAdminAction(context as never, {
      action: "spanish_course.options",
      entity: "app_settings",
      summary: "Ändrade vilka spanskakurser och terminer som visas publikt",
      meta: data,
    });
    return { ok: true as const };
  });

/* ---------------------------------------------------------------------- */
/* Kurskatalog: kurser, terminer, schema och betalningar                    */
/* ---------------------------------------------------------------------- */

export type SpanishCourse = {
  id: string;
  name: string;
  description: string;
  priceEur: number;
  /** Tomt = kursen gäller alla öppna terminer. */
  terms: string[];
  audience: "adults" | "children";
  active: boolean;
  sortOrder: number;
};

export type SpanishCourseSession = {
  id: string;
  courseId: string;
  term: string;
  date: string;
  note: string;
};

type CourseRow = Record<string, unknown>;

function mapCourse(r: CourseRow): SpanishCourse {
  return {
    id: String(r["id"]),
    name: String(r["name"] ?? ""),
    description: String(r["description"] ?? ""),
    priceEur: Number(r["price_eur"] ?? 0),
    terms: Array.isArray(r["terms"]) ? (r["terms"] as string[]) : [],
    audience: r["audience"] === "children" ? "children" : "adults",
    active: r["active"] !== false,
    sortOrder: Number(r["sort_order"] ?? 0),
  };
}

function mapSession(r: CourseRow): SpanishCourseSession {
  return {
    id: String(r["id"]),
    courseId: String(r["course_id"]),
    term: String(r["term"] ?? ""),
    date: String(r["session_date"] ?? ""),
    note: String(r["note"] ?? ""),
  };
}

function publicClient() {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  return import("@supabase/supabase-js").then(({ createClient }) =>
    createClient(process.env["SUPABASE_URL"]!, key, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    }),
  );
}

/** Publikt: kurser som går att anmäla sig till, plus deras schemadagar. */
export const getSpanishCourseCatalog = createServerFn({ method: "GET" }).handler(async () =>
  cached("spanish-course-catalog", 5 * 60_000, async () => {
    const supabase = await publicClient();
    const [courses, sessions] = await Promise.all([
      supabase
        .from("spanish_courses")
        .select("id, name, description, price_eur, terms, audience, active, sort_order")
        .eq("active", true)
        .order("sort_order", { ascending: true }),
      supabase
        .from("spanish_course_sessions")
        .select("id, course_id, term, session_date, note")
        .order("session_date", { ascending: true }),
    ]);
    return {
      courses: ((courses.data ?? []) as CourseRow[]).map(mapCourse),
      sessions: ((sessions.data ?? []) as CourseRow[]).map(mapSession),
    };
  }),
);

/** Admin: hela katalogen, även avstängda kurser. */
export const listSpanishCourses = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertLightAdmin(context as never, "/admin/spanskakurser");
    const [courses, sessions] = await Promise.all([
      context.supabase
        .from("spanish_courses")
        .select("id, name, description, price_eur, terms, audience, active, sort_order")
        .order("sort_order", { ascending: true }),
      context.supabase
        .from("spanish_course_sessions")
        .select("id, course_id, term, session_date, note")
        .order("session_date", { ascending: true }),
    ]);
    return {
      courses: ((courses.data ?? []) as CourseRow[]).map(mapCourse),
      sessions: ((sessions.data ?? []) as CourseRow[]).map(mapSession),
    };
  });

const courseInput = z.object({
  id: z.string().uuid().nullable().default(null),
  name: z.string().trim().min(2, "Fyll i kursens namn").max(120),
  description: z.string().trim().max(300).default(""),
  priceEur: z.number().int().min(0).max(10000),
  terms: z.array(z.string().trim().max(60)).max(20).default([]),
  audience: z.enum(["adults", "children"]).default("adults"),
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
});

/** Admin: skapa eller ändra en kurs i katalogen. */
export const saveSpanishCourse = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => courseInput.parse(data))
  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/spanskakurser");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const row = {
      name: data.name,
      description: data.description,
      price_eur: data.priceEur,
      terms: data.terms,
      audience: data.audience,
      active: data.active,
      sort_order: data.sortOrder,
    };
    const query = data.id
      ? supabaseAdmin.from("spanish_courses").update(row).eq("id", data.id)
      : supabaseAdmin.from("spanish_courses").insert(row);
    const { error } = await query;
    if (error) {
      console.error("spanish course save failed", error);
      throw new Error("Kursen kunde inte sparas.");
    }
    invalidateCached("spanish-course-catalog");
    await (await import("@/lib/audit.server")).logAdminAction(context as never, {
      action: data.id ? "spanish_course.course.update" : "spanish_course.course.create",
      entity: "spanish_course",
      entityId: data.id,
      summary: `${data.id ? "Ändrade" : "Skapade"} spanskakursen ${data.name}`,
      meta: row,
    });
    return { ok: true as const };
  });

/** Admin: ta bort en kurs ur katalogen (anmälningar behålls). */
export const deleteSpanishCourse = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertPageAction(context as never, "/admin/spanskakurser", "delete");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("spanish_courses").delete().eq("id", data.id);
    if (error) throw new Error("Kursen kunde inte tas bort.");
    invalidateCached("spanish-course-catalog");
    await (await import("@/lib/audit.server")).logAdminAction(context as never, {
      action: "spanish_course.course.delete",
      entity: "spanish_course",
      entityId: data.id,
      severity: "warning",
      summary: "Tog bort en spanskakurs ur katalogen",
    });
    return { ok: true as const };
  });

/** Admin: sätter exakt vilka dagar en kurs går under en termin. */
export const setSpanishCourseSessions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        courseId: z.string().uuid(),
        term: z.string().trim().min(1).max(60),
        dates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(200),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/spanskakurser");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const del = await supabaseAdmin
      .from("spanish_course_sessions")
      .delete()
      .eq("course_id", data.courseId)
      .eq("term", data.term);
    if (del.error) throw new Error("Schemat kunde inte sparas.");
    if (data.dates.length) {
      const { error } = await supabaseAdmin.from("spanish_course_sessions").insert(
        [...new Set(data.dates)].map((d) => ({
          course_id: data.courseId,
          term: data.term,
          session_date: d,
        })),
      );
      if (error) throw new Error("Schemat kunde inte sparas.");
    }
    invalidateCached("spanish-course-catalog");
    await (await import("@/lib/audit.server")).logAdminAction(context as never, {
      action: "spanish_course.schedule",
      entity: "spanish_course",
      entityId: data.courseId,
      summary: `Uppdaterade schemat för ${data.term} (${plural(data.dates.length, "tillfälle", "tillfällen")})`,
    });
    return { ok: true as const };
  });

/** Admin: markera en deltagare som betald eller obetald. */
export const setSpanishCoursePaid = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        paid: z.boolean(),
        note: z.string().trim().max(200).default(""),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/spanskakurser");
    const { error } = await context.supabase
      .from("spanish_course_applications")
      .update({
        paid_at: data.paid ? new Date().toISOString() : null,
        paid_note: data.paid ? data.note : null,
      })
      .eq("id", data.id);
    if (error) throw new Error("Betalningen kunde inte sparas.");
    await (await import("@/lib/audit.server")).logAdminAction(context as never, {
      action: "spanish_course.paid",
      entity: "spanish_course_application",
      entityId: data.id,
      summary: data.paid ? "Markerade anmälan som betald" : "Tog bort betalningsmarkeringen",
    });
    return { ok: true as const };
  });
